import { supabase } from "@/lib/supabaseClient";

/** 단어 진행도 삭제 (단건 — undo 등) */
export async function deleteWordProgress(userId: string, wordId: string) {
  const { error } = await supabase.from("user_word_progress").delete().eq("user_id", userId).eq("word_id", wordId);

  if (error) {
    throw error;
  }
}

/** 세트 단위 진행도 삭제 (다시 시작하기) */
export async function deleteWordProgressBySet(userId: string, setId: string) {
  const { error } = await supabase.rpc("delete_word_progress_by_set", {
    p_user_id: userId,
    p_set_id: setId,
  });

  if (error) {
    throw error;
  }
}

/** 단어 진행도 upsert (알고있음/학습중) */
export async function upsertWordProgress(userId: string, wordId: string, status: "known" | "learning") {
  const { error } = await supabase.from("user_word_progress").upsert(
    {
      user_id: userId,
      word_id: wordId,
      status,
      last_studied_at: new Date().toISOString(),
    },
    { onConflict: "user_id,word_id" },
  );

  if (error) {
    throw error;
  }
}

const PAGE_SIZE = 1000;

/** 세트별 진행된 단어 ID 조회 (known + learning) */
export async function getWordProgressBySet(
  userId: string,
  setId: string,
): Promise<{ knownWordIds: Set<string>; learningWordIds: Set<string> }> {
  const allData: Array<{ word_id: string; status: string }> = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from("user_word_progress")
      .select("word_id, status, words!inner(set_id)")
      .eq("user_id", userId)
      .in("status", ["known", "learning"])
      .eq("words.set_id", setId)
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      throw error;
    }

    allData.push(...(data ?? []));

    if (!data || data.length < PAGE_SIZE) {
      break;
    }

    from += PAGE_SIZE;
  }

  const knownWordIds = new Set<string>();
  const learningWordIds = new Set<string>();

  for (const row of allData) {
    if (row.status === "known") {
      knownWordIds.add(row.word_id);
    } else {
      learningWordIds.add(row.word_id);
    }
  }

  return { knownWordIds, learningWordIds };
}

export type SetProgressCounts = {
  knownCount: number;
  learningCount: number;
};

/** 세트별 진행된(known + learning) 단어 수 조회 */
export async function getLearnedCountsByUser(userId: string): Promise<Record<string, SetProgressCounts>> {
  const allData: Array<{ word_id: string; status: string; words: unknown }> = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from("user_word_progress")
      .select("word_id, status, words!inner(set_id)")
      .eq("user_id", userId)
      .in("status", ["known", "learning"])
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      throw error;
    }

    allData.push(...(data ?? []));

    if (!data || data.length < PAGE_SIZE) {
      break;
    }

    from += PAGE_SIZE;
  }

  const counts: Record<string, SetProgressCounts> = {};
  for (const row of allData) {
    const setId = (row.words as unknown as { set_id: string }).set_id;
    if (!counts[setId]) {
      counts[setId] = { knownCount: 0, learningCount: 0 };
    }
    if (row.status === "known") {
      counts[setId].knownCount++;
    } else {
      counts[setId].learningCount++;
    }
  }
  return counts;
}
